// components/ReportViewModal.jsx
import React from 'react';
import { Modal, Button, Spin } from 'antd';
import { PrinterOutlined, MedicineBoxOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import { printReportService } from '../../utils/PrintReport';
import { formatMedicalReport } from '../../utils/reportFormatters';

const ReportViewModal = ({ visible, onClose, report, patient }) => {
  const handlePrintReport = () => {
    if (report) {
      printReportService.printEnhancedReport(report, patient);
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-4 pb-6 border-b border-gray-200">
          <img
            src='https://res.cloudinary.com/dk2uh7sho/image/upload/v1754402435/WhatsApp_Image_2025-07-29_at_7.40.50_PM_hogkwx.jpg'
            width={48}
            height={48}
            className='rounded-xl object-cover border-2 border-slate-200'
            alt="DrEasy Logo"
          />
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Medical Analysis Report 
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Patient:</span>
                <span className="font-semibold text-gray-900">{patient?.fullName}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">ID:</span>
                <span className="font-semibold text-gray-900">{patient?.id}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Generated:</span>
                <span className="font-medium text-gray-700">
                  {report ? new Date(report.createdAt).toLocaleString() : ''}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-gray-500">Status:</span>
                <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Historical Report
                </span>
              </div>
            </div>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={[
        <div key="footer" className="flex justify-between items-center w-full pt-4 border-t border-gray-200">
          <div className="text-xs text-gray-500 flex items-center gap-2">
            <MedicineBoxOutlined />
            Powered by JOOD AI Medical Intelligence
          </div>
          <div className="flex gap-3">
            <Button
              icon={<PrinterOutlined />}
              onClick={handlePrintReport}
              className="border-slate-500 text-slate-600 hover:bg-slate-50"
            >
              Print Report
            </Button>
            <Button
              type="primary"
              onClick={onClose}
              className="bg-slate-600 border-slate-600"
            >
              Close
            </Button>
          </div>
        </div>
      ]}
      width="95%"
      style={{ maxWidth: '900px', top: 20 }}
      bodyStyle={{
        maxHeight: '75vh',
        overflowY: 'auto',
        padding: 0,
        background: '#f8fafc'
      }}
      className="medical-report-modal"
    >
      {report ? (
        <div className="p-6">
          {/* Academic Medical Report Header */}
          <div className="bg-slate-600 text-white text-center py-4 mb-0 shadow-sm">
            <h1 className="text-xl font-bold mb-1 uppercase tracking-wider">Medical Analysis Report</h1>
            <p className="text-slate-200 text-sm">Clinical Data Analysis & Assessment</p>
          </div>

          {/* Patient Information Table - Academic Style */}
          <div className="bg-white border border-gray-300 mb-6">
            <div className="bg-slate-600 text-white text-center py-2 px-4 font-bold text-sm uppercase tracking-wider">
              Patient Information
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-gray-300">
              <div className="p-3 border-r border-gray-300 bg-gray-50">
                <span className="font-semibold text-gray-700">Patient Name:</span>
                <span className="ml-2 text-gray-900">{patient?.fullName || 'N/A'}</span>
              </div>
              <div className="p-3 bg-white">
                <span className="font-semibold text-gray-700">Patient ID:</span>
                <span className="ml-2 text-gray-900">{patient?.id || 'N/A'}</span>
              </div>
              <div className="p-3 border-r border-gray-300 border-t border-gray-300 bg-gray-50">
                <span className="font-semibold text-gray-700">Report Date:</span>
                <span className="ml-2 text-gray-900">{report 
                  ? new Date(report.createdAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })
                  : new Date().toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })
                }</span>
              </div>
              <div className="p-3 border-t border-gray-300 bg-white">
                <span className="font-semibold text-gray-700">Analysis Time:</span>
                <span className="ml-2 text-gray-900">{report 
                  ? new Date(report.createdAt).toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit', 
                      hour12: true 
                    })
                  : new Date().toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit', 
                      hour12: true 
                    })
                }</span>
              </div>
            </div>
          </div>

          {/* Report Content - Academic Style */}
          <div
            id="historical-medical-report-content"
            className="bg-white border border-gray-300 shadow-sm academic-report-content p-1"
            style={{
              direction: 'rtl',
              textAlign: 'right',
              fontFamily: 'Times New Roman, Georgia, serif',
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#374151'
            }}
            dangerouslySetInnerHTML={{
              __html: formatMedicalReport   (report.summaryHtml || '')
            }}
          />

          {/* Academic Footer */}
          <div className="mt-6 bg-gray-50 border border-gray-300 p-4 text-center">
            <div className="mt-3 border-gray-300 text-xs text-gray-500">
              Generated by JOOD AI Medical Intelligence System | 
              Report ID: DR-{patient?.id}-{report 
                ? new Date(report.createdAt).toISOString().slice(0,10)
                : new Date().toISOString().slice(0,10)
              }
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-16 px-6">
          <div className="mb-6">
            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center mb-4 animate-pulse">
              <MedicineBoxOutlined className="text-3xl text-white" />
            </div>
            <Spin size="large" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            🔍 Loading Medical Report
          </h3>
          <p className="text-gray-600 mb-4 max-w-md mx-auto">
            Retrieving the historical medical analysis report from our database.
          </p>
          <div className="text-sm text-gray-500">
            Please wait while we load the report data
          </div>
        </div>
      )}
    </Modal>
  );
};

ReportViewModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  report: PropTypes.object,
  patient: PropTypes.object.isRequired
};

export default ReportViewModal;